import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend, FunnelChart, Funnel, LabelList } from "recharts";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/common";
import { LANG_NAMES } from "@/lib/format";
import IndiaHeatmap from "@/components/IndiaHeatmap";
import api from "@/lib/api";

const OUT = "#3B82F6", INB = "#A855F7", IND = "#6366F1", GRN = "#22C55E";
const PIE = ["#6366F1", "#A855F7", "#3B82F6", "#22C55E", "#F59E0B", "#EF4444"];

const ChartCard = ({ title, children, className = "" }) => (
  <Card className={`border shadow-none p-5 ${className}`}>
    <h3 className="font-semibold font-display text-sm mb-4">{title}</h3>
    {children}
  </Card>
);

export default function Analytics() {
  const [range, setRange] = useState("7d");
  const [ov, setOv] = useState(null);
  const [out, setOut] = useState(null);
  const [inb, setInb] = useState(null);
  const [leads, setLeads] = useState(null);
  const [ai, setAi] = useState(null);

  useEffect(() => {
    api.get(`/analytics/overview?range=${range}`).then((r) => setOv(r.data));
    api.get("/analytics/outbound").then((r) => setOut(r.data));
    api.get("/analytics/inbound").then((r) => setInb(r.data));
    api.get("/analytics/leads").then((r) => setLeads(r.data));
    api.get("/analytics/ai").then((r) => setAi(r.data));
  }, [range]);

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Combined inbound + outbound performance">
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40" data-testid="range-picker"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6" data-testid="analytics-tabs">
          {["overview", "outbound", "inbound", "leads", "ai"].map((t) => (
            <TabsTrigger key={t} value={t} className="capitalize" data-testid={`tab-${t}`}>{t === "ai" ? "AI Performance" : t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          {!ov ? <Skeleton className="h-96" /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Call Volume: Outbound vs Inbound">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ov.volume_by_day}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
                    <XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                    <Bar dataKey="outbound" stackId="a" fill={OUT} radius={[0,0,0,0]} />
                    <Bar dataKey="inbound" stackId="a" fill={INB} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Qualification Funnel (Combined)">
                <ResponsiveContainer width="100%" height={260}>
                  <FunnelChart><Tooltip />
                    <Funnel dataKey="value" data={ov.funnel} isAnimationActive>
                      <LabelList position="right" fill="#64748b" stroke="none" dataKey="stage" fontSize={12} />
                      {ov.funnel.map((e, i) => <Cell key={e.stage} fill={PIE[i % PIE.length]} />)}
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Direction Split">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={ov.direction_split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {ov.direction_split.map((e, i) => <Cell key={e.name} fill={i === 0 ? OUT : INB} />)}
                    </Pie><Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Language Distribution">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={ov.language_dist.map(d => ({...d, name: LANG_NAMES[d.name] || d.name}))} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {ov.language_dist.map((e, i) => <Cell key={e.name} fill={PIE[i % PIE.length]} />)}
                    </Pie><Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Hourly: Outbound Answer % + Inbound Volume" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={ov.heatmap}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
                    <XAxis dataKey="hour" fontSize={11} /><YAxis fontSize={12} /><Tooltip /><Legend />
                    <Bar dataKey="outbound_answer" name="Outbound Answer %" fill={OUT} radius={[4,4,0,0]} />
                    <Bar dataKey="inbound_volume" name="Inbound Volume" fill={INB} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="A/B Variant Comparison" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={ov.ab_comparison}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
                    <XAxis dataKey="variant" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                    <Bar dataKey="qualified" fill={IND} radius={[4,4,0,0]} />
                    <Bar dataKey="converted" fill={GRN} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </TabsContent>

        <TabsContent value="outbound">
          {!out ? <Skeleton className="h-96" /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Answer Rate & Avg Duration Trend" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={out.answer_rate_trend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                    <Line type="monotone" dataKey="answer_rate" stroke={OUT} strokeWidth={2} />
                    <Line type="monotone" dataKey="avg_duration" stroke={IND} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Drop-off Analysis">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={out.dropoff} layout="vertical"><XAxis type="number" fontSize={12} /><YAxis type="category" dataKey="stage" fontSize={11} width={80} /><Tooltip /><Bar dataKey="value" fill={OUT} radius={[0,4,4,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Best / Worst Time Slots">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={out.time_slots}><CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} /><XAxis dataKey="slot" fontSize={10} /><YAxis fontSize={12} /><Tooltip /><Bar dataKey="rate" fill={IND} radius={[4,4,0,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <Card className="border shadow-none p-6 lg:col-span-2 flex items-center justify-around">
                <div className="text-center"><div className="text-3xl font-bold font-display text-indigo-600">{out.attempts_to_qualify}</div><div className="text-xs text-slate-500 mt-1">Avg Attempts to Qualification</div></div>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="inbound">
          {!inb ? <Skeleton className="h-96" /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Inbound Volume by Hour" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={inb.by_hour}><CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} /><XAxis dataKey="hour" fontSize={11} /><YAxis fontSize={12} /><Tooltip /><Bar dataKey="volume" fill={INB} radius={[4,4,0,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              {[["Missed Call Rate", `${inb.missed_rate}%`], ["Avg Wait Time", `${inb.avg_wait}s`], ["Conversion Rate", `${inb.conversion_rate}%`], ["First Call Resolution", `${inb.fcr_rate}%`]].map(([l, v]) => (
                <Card key={l} className="border shadow-none p-6 text-center"><div className="text-3xl font-bold font-display text-purple-600">{v}</div><div className="text-xs text-slate-500 mt-1">{l}</div></Card>
              ))}
              <ChartCard title="Top Inbound Source Cities" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={inb.top_cities}><CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} /><XAxis dataKey="city" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Bar dataKey="count" fill={INB} radius={[4,4,0,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
        </TabsContent>

        <TabsContent value="leads">
          {!leads ? <Skeleton className="h-96" /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Top 10 Cities by Qualification">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={leads.top_cities} layout="vertical"><XAxis type="number" fontSize={12} /><YAxis type="category" dataKey="city" fontSize={11} width={90} /><Tooltip /><Legend /><Bar dataKey="count" name="Total" fill={IND} radius={[0,4,4,0]} /><Bar dataKey="qualified" name="Qualified" fill={GRN} radius={[0,4,4,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Interest / Product Breakdown">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart><Pie data={leads.interests} dataKey="value" nameKey="name" outerRadius={90}>{leads.interests.map((e, i) => <Cell key={e.name} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip /></PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Source: Outbound vs Inbound Origin">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart><Pie data={leads.source_split} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>{leads.source_split.map((e, i) => <Cell key={e.name} fill={[OUT, INB, "#64748b"][i]} />)}</Pie><Tooltip /><Legend /></PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Leads Heatmap by State">
                <IndiaHeatmap data={leads.state_heatmap} />
              </ChartCard>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai">
          {!ai ? <Skeleton className="h-96" /> : (
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Akriti Accuracy by Language (Out vs In)" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ai.accuracy_by_language.map(d => ({...d, language: LANG_NAMES[d.language] || d.language}))}><CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} /><XAxis dataKey="language" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend /><Bar dataKey="outbound" fill={OUT} radius={[4,4,0,0]} /><Bar dataKey="inbound" fill={INB} radius={[4,4,0,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Response Quality Trend">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={ai.quality_trend}><CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend /><Line type="monotone" dataKey="outbound" stroke={OUT} strokeWidth={2} /><Line type="monotone" dataKey="inbound" stroke={INB} strokeWidth={2} /></LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Most Common Objections">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={ai.objections} layout="vertical"><XAxis type="number" fontSize={12} /><YAxis type="category" dataKey="name" fontSize={11} width={110} /><Tooltip /><Bar dataKey="value" fill="#EF4444" radius={[0,4,4,0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <Card className="border shadow-none p-6 text-center"><div className="text-3xl font-bold font-display text-amber-500">{ai.avg_criteria}</div><div className="text-xs text-slate-500 mt-1">Avg Criteria Collected / Call</div></Card>
              <Card className="border shadow-none p-6 text-center"><div className="text-3xl font-bold font-display text-indigo-600">{ai.handover_rate}%</div><div className="text-xs text-slate-500 mt-1">Human Handover Rate</div></Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
